import { FC } from 'react';

import { ChipChoice } from '@telialt/common-chip-choice/src';
import { MessagesType } from '@telialt/common-cms/src/model';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { ContractType } from '@telialt/models/src/EshopModel';
import { useComponentId } from '@telialt/utils/src/hooks';

import { ContactChoiceContainer, Container, Title } from './ContractTypeSelector.styles';
import { useMutateContractType } from './useMutateContractType';

export const ContractTypeSelector: FC = () => {
    const { cart } = useCartQuery();
    const { t } = useTranslations();
    const { mutateContractType, isLoadingMutateContractType } = useMutateContractType();
    const id = useComponentId('contract-type-selector');

    const possibleContractTypes = [
        cart.possibleContractTypes?.find((s) => s === ContractType.WithPlan),
        cart.possibleContractTypes?.find((s) => s === ContractType.Renting),
        cart.possibleContractTypes?.find((s) => s === ContractType.WithoutPlan),
    ].filter(Boolean);

    if (possibleContractTypes.length <= 1) {
        return null;
    }

    const textByContractType: Record<ContractType, keyof MessagesType> = {
        [ContractType.WithPlan]: 'product.plan.label.with_plan',
        [ContractType.WithoutPlan]: 'product.plan.label.without_plan',
        [ContractType.Renting]: 'product.plan.label.renting',
    };

    return (
        <Container data-test={ESHOP_TEST_ID.B2C_CONTRACT_TYPE_SELECTOR}>
            <Title tag="h3" variant="title-100" id={id}>
                {t['product.detail.page.payment.plan.selector.title']}
            </Title>
            <ContactChoiceContainer aria-labelledby={id} role="radiogroup">
                {possibleContractTypes.map((contractType) => (
                    <ChipChoice
                        key={contractType}
                        disabled={isLoadingMutateContractType && cart?.contractType !== contractType}
                        checked={cart.contractType === contractType}
                        onChange={() => mutateContractType(contractType as ContractType)}
                        name={id}
                    >
                        {t[textByContractType[contractType]]}
                    </ChipChoice>
                ))}
            </ContactChoiceContainer>
        </Container>
    );
};
